// Kept local: js/utility pulls in the service layer and the common elements.
const numberFormat = new Intl.NumberFormat("en-GB");
const formatNumber = (number) => numberFormat.format(number);

/**
 * The most records that can be ticked individually, and the most that an action
 * which works from record ids (Bulk Edit, Activate / Deactivate) can apply to.
 * Matches EntitySearchRequest.MaxSelectedRecords on the server.
 */
export const MAX_SELECTED_RECORDS = 1000;

/**
 * The New button beside a table's "..." menu: the same light, bordered look and
 * height as the menu's button, with dark text.
 */
export const TABLE_ACTION_BUTTON_CLASS =
  "btn border rounded-2 bg-white text-dark fw-medium hover-bg-light d-inline-flex align-items-center table-action-btn";

/**
 * The number of records a selection applies to: every matching record when all
 * pages are selected, otherwise the records ticked.
 */
export function getSelectedCount(
  allPagesSelected,
  recordsSelected,
  totalRecords
) {
  if (allPagesSelected) {
    return totalRecords || 0;
  }
  return recordsSelected ? recordsSelected.length : 0;
}

/**
 * Whether an action that needs the record ids can run for this selection.
 */
export function isWithinSelectionLimit(selectedCount) {
  return selectedCount <= MAX_SELECTED_RECORDS;
}

/**
 * The state of the "select this page" checkbox.
 */
export function getPageSelectionState(
  pageRecords,
  recordsSelected,
  allPagesSelected
) {
  const records = pageRecords || [];
  const selectedIds = new Set(
    (recordsSelected || []).map((record) => record.Id)
  );
  const selectedOnPage = records.filter((record) =>
    selectedIds.has(record.Id)
  ).length;
  const unselectedOnPage = records.length - selectedOnPage;

  const allOnPageSelected =
    allPagesSelected ||
    (records.length > 0 && selectedOnPage === records.length);

  return {
    allOnPageSelected,
    someOnPageSelected: !allOnPageSelected && selectedOnPage > 0,
    // Ticking the rest of the page must not take the selection past the limit.
    disabled:
      allPagesSelected ||
      records.length === 0 ||
      (!allOnPageSelected &&
        selectedIds.size + unselectedOnPage > MAX_SELECTED_RECORDS),
  };
}

/**
 * Ticks every record on the page, or unticks them all if they already are.
 */
export function togglePageRecords(pageRecords, recordsSelected) {
  const records = pageRecords || [];
  const selected = recordsSelected || [];
  const selectedIds = new Set(selected.map((record) => record.Id));
  const allOnPageSelected =
    records.length > 0 &&
    records.every((record) => selectedIds.has(record.Id));

  if (allOnPageSelected) {
    const pageIds = new Set(records.map((record) => record.Id));
    return selected.filter((record) => !pageIds.has(record.Id));
  }

  const additions = records.filter(
    (record) => !selectedIds.has(record.Id)
  );
  return [...selected, ...additions].slice(0, MAX_SELECTED_RECORDS);
}

/**
 * Ticks or unticks a single record, never going past the limit.
 */
export function toggleRecord(record, recordsSelected) {
  const selected = recordsSelected || [];
  if (selected.some((s) => s.Id === record.Id)) {
    return selected.filter((s) => s.Id !== record.Id);
  }
  if (selected.length >= MAX_SELECTED_RECORDS) {
    return selected;
  }
  return [...selected, record];
}

export function formatSelectedRecordCount(count) {
  return `the ${formatNumber(count)} selected ${
    count === 1 ? "record" : "records"
  }`;
}

export function formatRecordCount(count) {
  return `${formatNumber(count)} ${
    count === 1 ? "record" : "records"
  }`;
}

export function getSelectionLimitMessage(selectedCount) {
  return `This action can apply to a maximum of ${formatNumber(
    MAX_SELECTED_RECORDS
  )} records. You have selected ${formatNumber(
    selectedCount
  )}. Refine your search or select fewer records.`;
}
